// src/pages/NewTransferPage.js
import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Button, Typography, Grid, Select, MenuItem,
    InputLabel, FormControl, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, CircularProgress, Alert, Autocomplete, TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import AuthContext from '../context/AuthContext.js';

const NewTransferPage = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    // --- ESTADOS ---
    const [originStore, setOriginStore] = useState(user?.stores?.[0] || '');
    const [destinationStore, setDestinationStore] = useState('');
    const [transferItems, setTransferItems] = useState([]);
    
    // Estados para el formulario de añadir ítem
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    
    // Estados para catálogos y UI
    const [allStores, setAllStores] = useState([]);
    const [inventoryProducts, setInventoryProducts] = useState([]); // Productos del inventario de origen
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // --- CARGA DE DATOS ---
    useEffect(() => {
        const fetchStores = async () => {
            try {
                const response = await api.get('/stores');
                setAllStores(response.data);
            } catch (err) { console.error(err); }
        };
        fetchStores();
    }, []);

    useEffect(() => {
        const fetchInventory = async () => {
            if (!originStore) return;
            setLoading(true);
            try {
                const response = await api.get(`/inventory/store/${originStore}`);
                setInventoryProducts(response.data);
            } catch (err) {
                console.error(err);
                setInventoryProducts([]);
            } finally {
                setLoading(false);
            }
        };
        fetchInventory();
    }, [originStore]);

    // --- MANEJADORES DE EVENTOS ---
    const handleAddItem = () => {
        if (!selectedProduct || quantity <= 0) {
            setError('Selecciona un producto y una cantidad válida.');
            return;
        }
        if (quantity > selectedProduct.quantity) {
            setError(`Stock insuficiente. Solo hay ${selectedProduct.quantity} unidades disponibles.`);
            return;
        }
        if (transferItems.some(item => item.product_id === selectedProduct.product_id)) {
            setError('El producto ya está en la lista de transferencia.');
            return;
        }

        setTransferItems([...transferItems, { ...selectedProduct, quantity_to_transfer: quantity }]);
        setSelectedProduct(null);
        setQuantity(1);
        setError('');
    };

    const handleRemoveItem = (productId) => {
        setTransferItems(transferItems.filter(item => item.product_id !== productId));
    };

    const handleSaveTransfer = async () => {
        setError('');
        if (!originStore || !destinationStore || transferItems.length === 0) {
            setError('Debes seleccionar tiendas de origen, destino y añadir al menos un producto.');
            return;
        }
        if (originStore === destinationStore) {
            setError('La tienda de origen y destino no pueden ser la misma.');
            return;
        }

        const payload = {
            origin_store_id: originStore,
            destination_store_id: destinationStore,
            items: transferItems.map(({ product_id, quantity_to_transfer }) => ({
                product_id,
                quantity: parseInt(quantity_to_transfer, 10),
            }))
        };
        
        try {
            setLoading(true);
            await api.post('/transfers', payload);
            alert('¡Transferencia creada exitosamente!');
            navigate('/transfers');
        } catch (err) {
            setError(err.response?.data?.message || 'Error al crear la transferencia.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h4" gutterBottom>Crear Nueva Transferencia</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* SECCIÓN 1: SELECCIÓN DE TIENDAS */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Tienda de Origen</InputLabel>
                            <Select value={originStore} label="Tienda de Origen" onChange={(e) => setOriginStore(e.target.value)}>
                                {allStores.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Tienda de Destino</InputLabel>
                            <Select value={destinationStore} label="Tienda de Destino" onChange={(e) => setDestinationStore(e.target.value)}>
                                {allStores.filter(s => s.id !== originStore).map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* SECCIÓN 2: AÑADIR ÍTEMS */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Añadir Productos a la Transferencia</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={7}>
                        <Autocomplete
                            options={inventoryProducts}
                            getOptionLabel={(option) => `${option.name} (Stock: ${option.quantity})`}
                            value={selectedProduct}
                            onChange={(event, newValue) => setSelectedProduct(newValue)}
                            renderInput={(params) => <TextField {...params} label="Buscar Producto en Origen" />}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth label="Cantidad a Transferir" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button fullWidth variant="contained" onClick={handleAddItem} sx={{ height: '56px' }}>Añadir</Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* SECCIÓN 3: LISTA DE ÍTEMS */}
            <Typography variant="h6" gutterBottom>Productos a Transferir</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead><TableRow><TableCell>Producto</TableCell><TableCell align="right">Cantidad</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead>
                    <TableBody>
                        {transferItems.map(item => (
                            <TableRow key={item.product_id}>
                                <TableCell>{item.name} ({item.sku})</TableCell>
                                <TableCell align="right">{item.quantity_to_transfer}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleRemoveItem(item.product_id)}><DeleteIcon color="error" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* SECCIÓN 4: GUARDAR */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" color="primary" size="large" onClick={handleSaveTransfer} disabled={loading}>
                    {loading ? 'Guardando...' : 'Confirmar Transferencia'}
                </Button>
            </Box>
        </Box>
    );
};

export default NewTransferPage;